// src/components/common/CodeEditorTab.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Paper, Box, Typography, Button, Tooltip, IconButton, Alert, useTheme, Stack
} from '@mui/material';
import {
  FileCopy as CopyIcon,
  Refresh as ResetIcon,
  InfoOutlined as InfoIcon, // Using Outlined for consistency
  Check as CheckIcon,
  HelpOutline as HelpIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const CodeEditorTab = ({
  code,
  initialCode,
  onCodeChange,
  onResetCode,
  language,
  title = "Code Editor",
  infoAlertText,
  documentationLink,
  showPreviewToggle = false, // This prop seems unused in current context, but kept for generality
  onPreviewToggle,         // This prop seems unused
  isPreviewVisible,        // This prop seems unused
  allowReset = true,
  isActiveTab, // IMPORTANT: To trigger layout on tab switch
  minEditorHeight = '500px', // Default minimum height for the editor area
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [codeCopied, setCodeCopied] = useState(false);
  const editorRef = useRef(null);

  const handleCopyCodeToClipboard = () => {
    if (!code || typeof code !== 'string' || code.trim() === '') { // Added check for empty/null code
        toast.info("Nothing to copy.");
        return;
    }
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };
  
  const hasUnsavedCodeChanges = code !== initialCode;

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;
    // Initial layout if it's already active (e.g. first tab)
    if (isActiveTab) {
      setTimeout(() => {
        editor?.layout();
      }, 100);
    }
  }

  useEffect(() => {
    if (isActiveTab && editorRef.current) {
      // Debounce or timeout to ensure layout happens after DOM is fully visible
      const timer = setTimeout(() => {
        editorRef.current?.layout();
      }, 150); // Slightly increased delay
      return () => clearTimeout(timer);
    }
  }, [isActiveTab]);

  return (
    <Paper 
        elevation={0} 
        sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            display: 'flex', 
            flexDirection: 'column', 
            flexGrow: 1, // Key for this component to take space in its parent flex container (TabPanel's Box)
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 1, 
            overflow: 'hidden', // Important for Monaco's own scrollbars
        }}
    >
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={1.5} 
        flexWrap="wrap" 
        gap={1}
      >
        <Typography variant="h6" sx={{fontSize: '1.1rem', fontWeight: 500}}>{title}</Typography>
        <Stack direction="row" spacing={0.5}>
          {showPreviewToggle && onPreviewToggle && ( // Kept for generality, though not used in widget editor
            <Button variant="outlined" size="small" onClick={onPreviewToggle} startIcon={isPreviewVisible ? <HideIcon /> : <ShowIcon />}>
              {isPreviewVisible ? "Hide Preview" : "Show Preview"}
            </Button>
          )}
          <Tooltip title="Copy Code">
            <IconButton onClick={handleCopyCodeToClipboard} size="small">
              {codeCopied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {allowReset && onResetCode && (
            <Tooltip title="Reset code to last saved state">
              <span>
                <IconButton onClick={onResetCode} size="small" disabled={!hasUnsavedCodeChanges && code === initialCode}>
                  <ResetIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {documentationLink && (
            <Tooltip title="View Documentation">
              <IconButton onClick={() => navigate(documentationLink)} size="small">
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {infoAlertText && <Alert severity="info" icon={<InfoIcon fontSize="inherit" />} sx={{ mb: 2, fontSize: '0.875rem', alignItems: 'center', py: 0.5 }}>{infoAlertText}</Alert>}
      
      <Box 
        sx={{ 
            flexGrow: 1,
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 1, 
            overflow: 'hidden',
            position: 'relative', // Good practice for editor containers
            minHeight: minEditorHeight, // Ensure a minimum visible height
        }}
      >
        <Editor
          language={language}
          value={code || ''} // Ensure value is not null/undefined
          onChange={onCodeChange}
          onMount={handleEditorDidMount}
          theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
          options={{ 
            minimap: { enabled: false }, 
            scrollBeyondLastLine: false, 
            fontSize: 13, // Slightly smaller for more code visibility
            automaticLayout: true, // Crucial
            wordWrap: 'on',
            padding: { top: 10, bottom: 10 },
            tabSize: 2,
            insertSpaces: true,
            renderWhitespace: "boundary",
            scrollbar: { 
                verticalScrollbarSize: 10, 
                horizontalScrollbarSize: 10,
                alwaysConsumeMouseWheel: false // May help with page scrolling issues
            }
          }}
        />
      </Box>
      {hasUnsavedCodeChanges && allowReset && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'flex', alignItems: 'center', fontSize: '0.75rem' }}>
          <InfoIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Code has unsaved changes.
        </Typography>
      )}
    </Paper>
  );
};

export default CodeEditorTab;